import { lumi } from '@/lib/lumi'
import { notifyFormSubmission } from '@/lib/notifyFormSubmission'
import { AnimatePresence, motion } from 'framer-motion'
import { useState } from 'react'
import { usePageMeta } from '@/hooks/usePageMeta'

// ─── Structure ────────────────────────────────────────────────────────────────

interface Question {
  id: string
  label: string
}

interface Block {
  title: string
  subtitle: string
  questions: Question[]
}

// step 0 = identificação, steps 1-6 = blocos de perguntas
const BLOCKS: Block[] = [
  {
    title: 'Bloco 0',
    subtitle: 'Sua experiência no Ciclo 2',
    questions: [
      { id: 'q1', label: 'O que mudou durante os 21 dias do Ciclo 2?' },
      { id: 'q2', label: 'Você percebeu padrões de autossabotagem ou repetição se manifestando de forma diferente durante o ciclo?' },
      { id: 'q3', label: 'O que você sentiu ao ler o mapa e escutar a meditação do Ciclo 2?' },
      { id: 'q4', label: 'Houve situações no dia a dia em que você se pegou reagindo de forma diferente do habitual?' },
    ],
  },
  {
    title: 'Bloco 1',
    subtitle: 'Padrões e Relações',
    questions: [
      { id: 'q5',  label: 'O que ainda se repete em sua vida que você ainda não conseguiu identificar os padrões? Por exemplo: não me priorizo, sinto rejeição, abandono, traição.' },
      { id: 'q6',  label: 'Cite 3 pessoas que marcaram de forma negativa a sua vida até hoje e relate o que cada uma fez com você.' },
      { id: 'q7',  label: 'Qual a primeira pessoa que vem em sua mente que você deveria perdoar? Tem alguém que lhe decepcionou profundamente e que até hoje você carrega essa dor?' },
      { id: 'q8',  label: 'Houve alguém com quem você deveria ter expressado seus sentimentos de forma verdadeira e não conseguiu por medo, insegurança ou autojulgamento?' },
      { id: 'q9',  label: 'Dentro das relações que você vive, quer seja de amizade ou relacionamentos amorosos, normalmente você é a pessoa que mais doa ou a que mais recebe?' },
      { id: 'q10', label: 'Existem situações em que você não consegue ceder ou baixar a guarda mesmo sabendo que deveria? Quem fica dizendo para você mudar e mesmo assim você não muda?' },
    ],
  },
  {
    title: 'Bloco 2',
    subtitle: 'Família e Origens',
    questions: [
      { id: 'q11', label: 'Como é ou foi a sua relação com os seus pais? Se não teve contato com um deles ou ambos, como essa ausência impactou a sua vida e quem ocupou esse lugar?' },
      { id: 'q12', label: 'O que você gostaria que seus pais ou as pessoas que te criaram tivessem feito na sua vida e não fizeram?' },
      { id: 'q13', label: 'Como você viu a sua mãe ou figura materna priorizar a própria vida, desejos e sonhos? Se não teve essa referência, como a ausência dela moldou a sua visão sobre priorizar a si mesmo?' },
      { id: 'q14', label: 'Seu pai ou figura paterna viveu a vida que gostaria e cresceu em tudo que poderia? Se não teve essa referência, como a ausência dela moldou a sua relação com conquista e expansão?' },
    ],
  },
  {
    title: 'Bloco 3',
    subtitle: 'Feridas e Memórias',
    questions: [
      { id: 'q15', label: 'Tem alguma memória da infância ou adolescência que ainda hoje provoca dor, constrangimento ou tristeza quando vem à mente?' },
      { id: 'q16', label: 'Existe algo que você nunca contou para ninguém e que ainda carrega dentro de você?' },
      { id: 'q17', label: 'Olhando para o seu passado, tem algo que você se envergonha profundamente?' },
      { id: 'q18', label: 'Tem alguém que você deveria ter se dedicado mais no passado e de alguma forma se arrepende?' },
      { id: 'q19', label: 'Tem alguma situação que deixa você com aflição ou amargura só de pensar?' },
      { id: 'q20', label: 'Você é do tipo de pessoa que se vinga ou até deseja o mal para outras pessoas? Tem alguém que você queria que se desse mal e mesmo assim não pagou pelos seus erros?' },
    ],
  },
  {
    title: 'Bloco 4',
    subtitle: 'Vida e Propósito',
    questions: [
      { id: 'q21', label: 'Qual é a sua relação com o trabalho e com o seu propósito no mundo? Sente que o que faz hoje, seja uma profissão, um papel na família ou qualquer outra forma de contribuição, está alinhado com quem você é e com o que veio fazer?' },
      { id: 'q22', label: 'Você hoje é uma pessoa mais sociável ou mais retraída? Por quê?' },
      { id: 'q23', label: 'O que você quer conquistar que já tentou de todas as formas e não conseguiu?' },
      { id: 'q24', label: 'Você se sente feliz com a conquista das pessoas ao seu redor? Conhece alguém que tem sucesso e que você já poderia estar no nível dessa pessoa? Se sim, tem ideia do que ainda impede isso?' },
      { id: 'q25', label: 'Qual nota você daria para a sua própria evolução, inteligência e capacidade? Por quê?' },
    ],
  },
  {
    title: 'Bloco 5',
    subtitle: 'Projeção e Cocriação',
    questions: [
      { id: 'q26', label: 'O que mais lhe causa medo na vida? Por exemplo: medo do futuro, medo de não conseguir, medo do julgamento, medo de não viver o seu sonho, medo da rejeição.' },
      { id: 'q27', label: 'O que você quer conquistar em 30 dias?' },
      { id: 'q28', label: 'Como você se vê daqui a 10 anos?' },
      { id: 'q29', label: 'O que seria incrível que começasse a acontecer agora?' },
    ],
  },
]

const TOTAL_BLOCKS = 6
const ALL_TEXT_IDS = BLOCKS.flatMap(b => b.questions.map(q => q.id))

// ─── Page ────────────────────────────────────────────────────────────────────

export default function PageFormularioCiclo3() {
  usePageMeta({
    title: 'Formulário Ciclo 3 — Protocolo de Liberação e Cocriação',
    description: 'Formulário de aprofundamento do Ciclo 3 do Protocolo de Liberação e Cocriação.',
    ogImage: 'https://static.lumi.new/76/768cd02825460eb1548b4156e8d0d987.svg',
    ogUrl: 'https://jornada.reacendaluz.com.br/formulario/ciclo3/',
  })

  const accentColor = '#B9A3D9'
  const accentSoft  = '#D4C3EA'

  const [step, setStep]           = useState(0)
  const [direction, setDirection] = useState(1)

  const [name, setName]         = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [email, setEmail]       = useState('')
  const [answers, setAnswers]   = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted]   = useState(false)
  const [error, setError]       = useState('')

  function updateAnswer(id: string, value: string) {
    setAnswers(prev => ({ ...prev, [id]: value }))
    setError('')
  }

  const progressBlock   = step === 0 ? 0 : step
  const progressPercent = (progressBlock / TOTAL_BLOCKS) * 100

  function handleContinue() {
    setError('')

    if (step === 0) {
      const trimName     = name.trim()
      const trimWhatsapp = whatsapp.trim()
      const trimEmail    = email.trim()
      if (!trimName) {
        setError('Por favor informe seu nome.')
        document.getElementById('field-name')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }
      if (!trimWhatsapp) {
        setError('Por favor informe seu WhatsApp.')
        document.getElementById('field-whatsapp')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }
      if (!trimEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimEmail)) {
        setError('Por favor informe um email válido.')
        document.getElementById('field-email')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }
      advance()
      return
    }

    if (step >= 1 && step <= TOTAL_BLOCKS) {
      const block = BLOCKS[step - 1]
      for (const q of block.questions) {
        if (!(answers[q.id] || '').trim()) {
          setError('Por favor responda todas as perguntas deste bloco antes de continuar.')
          document.getElementById(`field-${q.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          return
        }
      }
      if (step === TOTAL_BLOCKS) {
        handleSubmit()
      } else {
        advance()
      }
      return
    }
  }

  function advance() {
    setDirection(1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setStep(prev => prev + 1)
  }

  async function handleSubmit() {
    setSubmitting(true)
    try {
      const now = new Date().toISOString()
      const payload: Record<string, unknown> = {
        name:      name.trim(),
        whatsapp:  whatsapp.trim(),
        email:     email.trim(),
        createdAt: now,
        updatedAt: now,
      }
      for (const id of ALL_TEXT_IDS) {
        payload[id] = (answers[id] || '').trim()
      }
      await lumi.entities.formulario_ciclo3.create(payload)
      notifyFormSubmission({ name: name.trim(), formName: 'Ciclo 3', submittedAt: now })
      setSubmitted(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      console.error('Erro ao enviar formulário Ciclo 3:', err)
      setError('Não foi possível enviar agora. Tente novamente em instantes.')
    } finally {
      setSubmitting(false)
    }
  }

  // ─── Success ───────────────────────────────────────────────────────────────

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0f14] flex items-center justify-center px-5 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="max-w-xl w-full text-center"
        >
          <p className="text-[10px] tracking-[0.4em] uppercase font-body mb-6" style={{ color: accentSoft }}>
            Protocolo de Liberação e Cocriação — Ciclo 3
          </p>

          <div
            className="w-16 h-16 rounded-full mx-auto mb-8 flex items-center justify-center"
            style={{ background: `${accentColor}18`, border: `1px solid ${accentSoft}55` }}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke={accentSoft}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h1 className="font-display type-hero mb-6" style={{ color: 'var(--ink,#e8e0d5)' }}>
            Suas respostas{' '}
            <em className="italic font-light" style={{ color: accentSoft }}>
              chegaram
            </em>
          </h1>

          <p className="font-body type-body mb-5" style={{ color: 'rgba(232,224,213,0.75)' }}>
            Obrigada por compartilhar com tanta honestidade e profundidade.
          </p>
          <p className="font-body type-body mb-5" style={{ color: 'rgba(232,224,213,0.7)' }}>
            Lerei tudo com cuidado e presença e em até{' '}
            <span className="numerals">72</span> horas você receberá sua meditação e Mapa de Revelações do Ciclo{' '}
            <span className="numerals">3</span>.
          </p>
          <p className="font-body type-body italic" style={{ color: 'rgba(232,224,213,0.55)' }}>
            Com amor e presença, Mônica, Reacenda Luz
          </p>

          <div
            className="h-px w-24 mx-auto mt-12"
            style={{ background: `linear-gradient(90deg, transparent, ${accentSoft}66, transparent)` }}
          />
          <p className="text-[10px] tracking-[0.3em] uppercase font-body mt-8" style={{ color: 'rgba(232,224,213,0.45)' }}>
            Mônica — Reacenda Luz
          </p>
        </motion.div>
      </div>
    )
  }

  // ─── Form ──────────────────────────────────────────────────────────────────

  const isLastStep = step === TOTAL_BLOCKS

  return (
    <div className="min-h-screen bg-[#0a0f14]">
      <div className="max-w-2xl mx-auto px-5 sm:px-6 py-14 sm:py-20">

        {/* ── Header fixo ── */}
        <motion.header
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-10 sm:mb-14"
        >
          <p className="text-[10px] tracking-[0.4em] uppercase font-body mb-5" style={{ color: accentSoft }}>
            Protocolo de Liberação e Cocriação
          </p>
          <h1 className="font-display type-hero mb-6" style={{ color: 'var(--ink,#e8e0d5)' }}>
            Ciclo{' '}
            <span className="numerals">3</span>
            {' '}—{' '}
            <em className="italic font-light" style={{ color: accentSoft }}>
              Raízes mais profundas
            </em>
          </h1>
          <p className="font-body type-body" style={{ color: 'rgba(232,224,213,0.7)', lineHeight: 1.8 }}>
            Você chegou ao Ciclo 3. Esse é o momento de ir ainda mais fundo.
            Responda com honestidade e profundidade.
            Suas respostas me ajudam a criar a meditação e o Mapa de Revelações específicos para essa camada da sua jornada.
          </p>
          <div
            className="h-px w-24 mx-auto mt-10"
            style={{ background: `linear-gradient(90deg, transparent, ${accentSoft}66, transparent)` }}
          />
        </motion.header>

        {/* ── Barra de progresso ── */}
        {step > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] tracking-[0.3em] uppercase font-body" style={{ color: accentSoft }}>
                Bloco{' '}
                <span className="numerals">{progressBlock}</span>
                {' '}de{' '}
                <span className="numerals">{TOTAL_BLOCKS}</span>
              </p>
              <p className="text-[11px] font-body numerals" style={{ color: 'rgba(232,224,213,0.4)' }}>
                {Math.round(progressPercent)}%
              </p>
            </div>

            <div
              className="w-full h-[2px] rounded-full overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.07)' }}
            >
              <motion.div
                className="h-full rounded-full"
                style={{ background: `linear-gradient(90deg, ${accentColor}, ${accentSoft})` }}
                initial={{ width: `${((progressBlock - 1) / TOTAL_BLOCKS) * 100}%` }}
                animate={{ width: `${progressPercent}%` }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
              />
            </div>

            <div className="flex justify-between mt-2">
              {Array.from({ length: TOTAL_BLOCKS }).map((_, i) => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full transition-all duration-300"
                  style={{
                    background: i < progressBlock ? accentColor : 'rgba(255,255,255,0.15)',
                  }}
                />
              ))}
            </div>
          </motion.div>
        )}

        {/* ── Conteúdo do step (animado) ── */}
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={{
              enter: (d: number) => ({ opacity: 0, x: d > 0 ? 40 : -40 }),
              center: { opacity: 1, x: 0 },
              exit:  (d: number) => ({ opacity: 0, x: d > 0 ? -40 : 40 }),
            }}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.45, ease: 'easeInOut' }}
          >

            {/* STEP 0 — Identificação */}
            {step === 0 && (
              <section className="space-y-5">
                <div className="mb-8">
                  <p className="text-[10px] tracking-[0.4em] uppercase font-body mb-3" style={{ color: accentSoft }}>
                    Antes de começar
                  </p>
                  <h2 className="font-display type-subtitle" style={{ color: 'var(--ink,#e8e0d5)' }}>
                    <em className="italic font-light">Quem é você?</em>
                  </h2>
                  <div
                    className="h-px w-16 mt-5"
                    style={{ background: `linear-gradient(90deg, ${accentSoft}55, transparent)` }}
                  />
                </div>

                <TextInput
                  id="field-name"
                  label="Seu nome completo"
                  value={name}
                  onChange={v => { setName(v); setError('') }}
                  accentSoft={accentSoft}
                />
                <TextInput
                  id="field-whatsapp"
                  label="Seu WhatsApp com DDD"
                  value={whatsapp}
                  onChange={v => { setWhatsapp(v); setError('') }}
                  accentSoft={accentSoft}
                />
                <TextInput
                  id="field-email"
                  label="Seu email"
                  type="email"
                  value={email}
                  onChange={v => { setEmail(v); setError('') }}
                  accentSoft={accentSoft}
                />
              </section>
            )}

            {/* STEPS 1-6 — Blocos de perguntas */}
            {step >= 1 && step <= TOTAL_BLOCKS && (() => {
              const block  = BLOCKS[step - 1]
              const offset = BLOCKS.slice(0, step - 1).reduce((s, b) => s + b.questions.length, 0)
              return (
                <section>
                  <div className="mb-10 text-center sm:text-left">
                    <p
                      className="text-[10px] tracking-[0.4em] uppercase font-body mb-2 numerals"
                      style={{ color: accentSoft }}
                    >
                      {block.title}
                    </p>
                    <h2 className="font-display type-subtitle" style={{ color: 'var(--ink,#e8e0d5)' }}>
                      <em className="italic font-light">{block.subtitle}</em>
                    </h2>
                    <div
                      className="h-px w-16 mt-5 mx-auto sm:mx-0"
                      style={{ background: `linear-gradient(90deg, ${accentSoft}55, transparent)` }}
                    />
                  </div>

                  <div className="space-y-8">
                    {block.questions.map((q, qIdx) => (
                      <QuestionField
                        key={q.id}
                        id={q.id}
                        number={offset + qIdx + 1}
                        label={q.label}
                        value={answers[q.id] || ''}
                        onChange={v => updateAnswer(q.id, v)}
                        accentSoft={accentSoft}
                      />
                    ))}
                  </div>
                </section>
              )
            })()}

          </motion.div>
        </AnimatePresence>

        {/* ── Erro + Botão de ação ── */}
        <div className="mt-10 pt-4">
          {error && (
            <motion.p
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-[13px] font-body text-center mb-4"
              style={{ color: '#f87171' }}
            >
              {error}
            </motion.p>
          )}
          <button
            type="button"
            onClick={handleContinue}
            disabled={submitting}
            className="block w-full text-center btn-primary px-4 sm:px-8 py-5 min-h-[56px] rounded-sm type-button tracking-[0.12em] sm:tracking-[0.18em] uppercase disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting
              ? 'Enviando...'
              : isLastStep
                ? 'Enviar minhas respostas'
                : 'Continuar'}
          </button>

          {isLastStep && (
            <p className="text-[11px] font-body text-center mt-6 italic" style={{ color: 'rgba(232,224,213,0.45)' }}>
              Suas respostas são tratadas com total sigilo.
            </p>
          )}
        </div>

        {/* ── Footer ── */}
        <footer className="mt-16 text-center">
          <div
            className="h-px w-24 mx-auto mb-6"
            style={{ background: `linear-gradient(90deg, transparent, ${accentSoft}44, transparent)` }}
          />
          <p className="text-[10px] tracking-[0.3em] uppercase font-body" style={{ color: 'rgba(232,224,213,0.45)' }}>
            Reacenda Luz · Mônica Maria
          </p>
        </footer>

      </div>
    </div>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function TextInput({
  id, label, value, onChange, accentSoft, type = 'text',
}: {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  accentSoft: string
  type?: string
}) {
  return (
    <div id={id}>
      <label
        className="block text-[10px] tracking-[0.3em] uppercase font-body mb-2"
        style={{ color: accentSoft }}
      >
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="input-field w-full rounded-sm px-4 py-3 font-body text-[16px]"
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.1)',
          color: 'var(--ink,#e8e0d5)',
        }}
      />
    </div>
  )
}

function QuestionField({
  id, number, label, value, onChange, accentSoft,
}: {
  id: string
  number: number
  label: string
  value: string
  onChange: (v: string) => void
  accentSoft: string
}) {
  return (
    <div id={`field-${id}`}>
      <div className="flex items-baseline gap-3 mb-3">
        <span className="numerals text-[12px] tracking-[0.2em] font-body flex-shrink-0" style={{ color: accentSoft }}>
          {String(number).padStart(2, '0')}
        </span>
        <label
          htmlFor={id}
          className="font-body type-body"
          style={{ color: 'var(--ink,#e8e0d5)', lineHeight: 1.6 }}
        >
          {label}
        </label>
      </div>
      <textarea
        id={id}
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={5}
        className="input-field w-full rounded-sm px-4 py-3 font-body text-[16px] resize-y"
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.1)',
          color: 'var(--ink,#e8e0d5)',
          lineHeight: 1.7,
          minHeight: 120,
        }}
        placeholder="Escreva sua resposta aqui..."
      />
    </div>
  )
}
